import React, { useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import MKTypography from "components/MKTypography";

function ReservationForm({ open, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    guests: "",
    notes: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `*New Reservation Request*%0A
--------------------------------%0A
*Name:* ${formData.name}%0A
*Email:* ${formData.email}%0A
*Phone:* ${formData.phone}%0A
*Date:* ${formData.date}%0A
*Time:* ${formData.time}%0A
*Number of Guests:* ${formData.guests}%0A
*Special Notes:* ${formData.notes}`;

    const whatsappUrl = `https://wa.me/60197562429?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "xl",
          backgroundColor: "transparent",
          backgroundImage: "none",
          boxShadow: "none",
        },
      }}
    >
      <MKBox
        position="relative"
        borderRadius="xl"
        overflow="hidden"
        backgroundColor="white"
        sx={{
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox
          position="absolute"
          width="100%"
          height="100%"
          top={0}
          left={0}
          sx={{
            background: ({ palette: { white } }) => white.main,
            opacity: 0.9,
          }}
        />
        <DialogTitle sx={{ position: "relative" }}>
          <MKBox display="flex" alignItems="center" justifyContent="space-between">
            <MKBox>
              <MKTypography variant="h3" color="info">
                Book a Table
              </MKTypography>
              <MKTypography variant="body2" color="text" opacity={0.8}>
                Fill in your details below
              </MKTypography>
            </MKBox>
            <IconButton
              onClick={onClose}
              aria-label="close"
              size="large"
              sx={{
                color: "text.secondary",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "rotate(90deg)",
                  color: "info.main",
                },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MKBox>
        </DialogTitle>
        <DialogContent sx={{ position: "relative" }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MKInput
                  variant="standard"
                  label="Your Name"
                  name="name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                  sx={{
                    "& .MuiInput-underline:before": { borderColor: "rgba(0,0,0,0.1)" },
                    "& .MuiInput-underline:hover:before": { borderColor: "rgba(0,0,0,0.2)" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MKInput
                  variant="standard"
                  type="email"
                  label="Email Address"
                  name="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleChange}
                  sx={{
                    "& .MuiInput-underline:before": { borderColor: "rgba(0,0,0,0.1)" },
                    "& .MuiInput-underline:hover:before": { borderColor: "rgba(0,0,0,0.2)" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MKInput
                  variant="standard"
                  label="Phone Number"
                  name="phone"
                  fullWidth
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  sx={{
                    "& .MuiInput-underline:before": { borderColor: "rgba(0,0,0,0.1)" },
                    "& .MuiInput-underline:hover:before": { borderColor: "rgba(0,0,0,0.2)" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MKInput
                  variant="standard"
                  type="number"
                  label="Number of Guests"
                  name="guests"
                  fullWidth
                  required
                  value={formData.guests}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                  sx={{
                    "& .MuiInput-underline:before": { borderColor: "rgba(0,0,0,0.1)" },
                    "& .MuiInput-underline:hover:before": { borderColor: "rgba(0,0,0,0.2)" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MKInput
                  variant="standard"
                  type="date"
                  label="Date"
                  name="date"
                  fullWidth
                  required
                  value={formData.date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiInput-underline:before": { borderColor: "rgba(0,0,0,0.1)" },
                    "& .MuiInput-underline:hover:before": { borderColor: "rgba(0,0,0,0.2)" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MKInput
                  variant="standard"
                  type="time"
                  label="Time"
                  name="time"
                  fullWidth
                  required
                  value={formData.time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiInput-underline:before": { borderColor: "rgba(0,0,0,0.1)" },
                    "& .MuiInput-underline:hover:before": { borderColor: "rgba(0,0,0,0.2)" },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MKInput
                  variant="standard"
                  label="Special Notes"
                  name="notes"
                  multiline
                  rows={3}
                  fullWidth
                  value={formData.notes}
                  onChange={handleChange}
                  sx={{
                    "& .MuiInput-underline:before": { borderColor: "rgba(0,0,0,0.1)" },
                    "& .MuiInput-underline:hover:before": { borderColor: "rgba(0,0,0,0.2)" },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MKBox display="flex" justifyContent="flex-end" mt={3} gap={2}>
                  <MKButton
                    variant="text"
                    color="info"
                    onClick={onClose}
                    sx={{
                      opacity: 0.8,
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    Cancel
                  </MKButton>
                  <MKButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    sx={{
                      background: "linear-gradient(45deg, #49a3f1, #1A73E8)",
                      boxShadow: "none",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px -6px rgba(26,115,232,0.4)",
                      },
                    }}
                  >
                    Confirm Reservation
                  </MKButton>
                </MKBox>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </MKBox>
    </Dialog>
  );
}

ReservationForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ReservationForm;
